// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Close } from '@mui/icons-material';
import { Dialog, Toolbar, Typography, IconButton, Box } from '@mui/material';
import { CollateralPosition } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Types/module';
import { TextInputLabel } from '../../../components/Form/TextInput';
import PartyDisplay from '../../../components/PartyDisplay';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { fmt } from '../../../utils/common';
import { Position } from './Positions';
import { GenericRow, MutualCollateralProps } from './types';

interface InstructDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  row?: GenericRow<MutualCollateralProps>;
}

export const SettledDetailsDialog: React.FC<InstructDetailsDialogProps> = ({
  open,
  onClose,
  row,
}) => {
  const party = useModuleParty();
  if (!row) {
    return null;
  }
  const p = row.payload.positions;
  const { createdAt } = row.payload;
  const createdAtDate = new Date(createdAt).toLocaleDateString('en-CA', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

  const settledPositions = p.map((s: CollateralPosition) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }} key={s.instrument.id}>
      <Typography variant="body2">{createdAtDate}</Typography>
      <Box sx={{ ml: 'auto', display: 'flex' }}>
        <Position
          instrumentId={s.instrument.id}
          isPledgor={s.pledgor === party}
          amount={fmt(s.amount, 2)}
        />
      </Box>
    </Box>
  ));
  return (
    <Dialog open={open} onClose={onClose}>
      <Toolbar>
        <Typography fontWeight="bold">Collateral detail</Typography>
        <IconButton size="small" onClick={onClose} sx={{ ml: 'auto' }}>
          <Close fontSize="small" color="action" />
        </IconButton>
      </Toolbar>

      <Box
        sx={{ minWidth: '500px', pl: 3, pb: 3, pr: 3, display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Agreement ID" />
          <Typography variant="body2">{row.payload.agreementId}</Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Instruct ID" />
          <Typography variant="body2">{row.payload.instructId || '--'}</Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Party A" />
          <Typography>
            <PartyDisplay party={row.payload.partyA} />
          </Typography>
        </Box>
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
          <TextInputLabel label="Party B" />
          <Typography>
            <PartyDisplay party={row.payload.partyB} />
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextInputLabel label="Settled Date" />
            <Box sx={{ ml: 'auto' }}>
              <TextInputLabel label="Settled amount" />
            </Box>
          </Box>
          {settledPositions}
        </Box>
      </Box>
    </Dialog>
  );
};
